// Actions on a single absence request (accept, reject, cancel, require
// correction) can be triggered from several independent surfaces (table row
// action, preview page, single/multi confirm dialogs) that each hold their own
// `useMutation` instance. `isPending` on one instance has no effect on the
// others, so nothing stops the same absenceRequestId from being submitted
// twice at once with the same (soon to be stale) eTag, which the backend
// answers with 412. This module-level set is shared by every mutation instance
// so a concurrent duplicate can be detected and rejected regardless of which
// component triggered it.
const inFlightAbsenceRequestIds = new Set<string>();

export const areAbsenceRequestsInFlight = (
  absenceRequestIds: string[]
): boolean => absenceRequestIds.some((id) => inFlightAbsenceRequestIds.has(id));

export const markAbsenceRequestsInFlight = (
  absenceRequestIds: string[]
): void => {
  absenceRequestIds.forEach((id) => inFlightAbsenceRequestIds.add(id));
};

export const clearAbsenceRequestsInFlight = (
  absenceRequestIds: string[]
): void => {
  absenceRequestIds.forEach((id) => inFlightAbsenceRequestIds.delete(id));
};

// Thrown by a mutationFn when it detects a duplicate concurrent submission via
// the guard above, so onError handlers can skip showing an error toast for it
// (it isn't a real failure, just a submission that was never sent).
export class DuplicateAbsenceRequestSubmissionError extends Error {
  constructor() {
    super("Absence request submission already in flight");
    this.name = "DuplicateAbsenceRequestSubmissionError";
  }
}

export const isDuplicateAbsenceRequestSubmissionError = (
  error: unknown
): error is DuplicateAbsenceRequestSubmissionError =>
  error instanceof DuplicateAbsenceRequestSubmissionError;

// Runs `request` only if none of the given ids is already in flight. The check
// and the mark happen synchronously (before the first `await`), so two calls
// made in the same tick cannot both pass the guard.
export const withAbsenceRequestsInFlightGuard = async <T>(
  absenceRequestIds: string[],
  request: () => Promise<T>
): Promise<T> => {
  if (areAbsenceRequestsInFlight(absenceRequestIds)) {
    throw new DuplicateAbsenceRequestSubmissionError();
  }

  markAbsenceRequestsInFlight(absenceRequestIds);

  try {
    return await request();
  } finally {
    clearAbsenceRequestsInFlight(absenceRequestIds);
  }
};
